import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Warehouse } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { POStatusBadge } from "@/components/POStatusBadge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { STOCK_STATUS_TONE, STOCK_STATUS_LABEL, WAREHOUSE_LABEL, VDB_EDIT_ROLES } from "@/lib/constants";
import { useRole } from "@/contexts/RoleContext";
import { VdbEditDialog } from "@/components/VdbEditDialog";
import { formatCurrency, formatDate, formatNumber } from "@/lib/format";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [editVdb, setEditVdb] = useState(false);
  const { role } = useRole();
  useEffect(() => { api.get(`/products/${id}`).then((r) => setProduct(r.data)); }, [id]);

  if (!product) return <div className="px-8 py-12">Laden…</div>;
  const isWebshop = product.source === "woocommerce";
  const qty = (v, d) => (product.stock_managed === false ? "—" : `${formatNumber(v, d)} ${product.unit}`);

  return (
    <div className="px-4 md:px-8 lg:px-12 py-8 md:py-10" data-testid="product-detail-page">
      <Link to="/voorraad" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" /> Terug naar voorraad
      </Link>
      <PageHeader
        eyebrow={product.category}
        title={product.name}
        description={[product.article_number,
          product.supplier && `Levertijd ${product.supplier.lead_time_days} dagen`,
          `Laatst bijgewerkt ${formatDate(product.last_updated)}`].filter(Boolean).join(" · ")}
        action={
          <div className="flex items-center gap-2">
            <StatusBadge tone={STOCK_STATUS_TONE[product.stock_status]}>
              {STOCK_STATUS_LABEL[product.stock_status]}
            </StatusBadge>
            <Button variant="outline" className="touch-tap" data-testid="edit-product">
              <Pencil className="w-4 h-4 mr-2" /> Bewerken
            </Button>
          </div>
        }
      />

      <Tabs defaultValue="overzicht" data-testid="product-tabs">
        <TabsList className="mb-6 h-auto flex-wrap">
          <TabsTrigger value="overzicht" data-testid="tab-overzicht" className="text-base px-6 py-3">Overzicht</TabsTrigger>
          <TabsTrigger value="voorraad" data-testid="tab-voorraad" className="text-base px-6 py-3">Voorraad per magazijn</TabsTrigger>
          <TabsTrigger value="bestellingen" data-testid="tab-bestellingen" className="text-base px-6 py-3">Openstaande bestellingen</TabsTrigger>
          <TabsTrigger value="verkoop" data-testid="tab-verkoop" className="text-base px-6 py-3">Verkoop en verbruik</TabsTrigger>
        </TabsList>

        <TabsContent value="overzicht">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <MetricCard label="Beschikbaar" value={qty(product.available_stock, 1)} tone={product.stock_status} />
            <MetricCard label="Totale voorraad" value={qty(product.stock_total, 1)} />
            <MetricCard label="Gereserveerd" value={qty(product.reserved_stock, 1)} />
            <MetricCard label="Minimum voorraad" value={`${formatNumber(product.minimum_stock, 0)} ${product.unit}`} />
            <MetricCard label="In bestelling" value={`${formatNumber(product.open_ordered, 0)} ${product.unit}`} tone={product.open_ordered > 0 ? "in-transit" : ""} />
            <MetricCard label="Advies inkoopaantal" value={`${formatNumber(product.reorder_quantity, 0)} ${product.unit}`} />
          </div>
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="surface p-6">
              <h3 className="text-lg font-semibold mb-4">Leverancier</h3>
              {product.supplier ? (
                <div className="space-y-2 text-sm">
                  <div className="text-base font-semibold">{product.supplier.name}</div>
                  <div>{product.supplier.contact_person}</div>
                  <div>{product.supplier.email} · {product.supplier.phone}</div>
                  <div className="text-muted-foreground">{product.supplier.country} · Levertijd {product.supplier.lead_time_days} dagen</div>
                </div>
              ) : <div className="text-sm text-muted-foreground">Niet gekoppeld.</div>}
            </div>
            <div className="surface p-6">
              <h3 className="text-lg font-semibold mb-4">Extra informatie</h3>
              <div className="space-y-2 text-sm">
                <Row label="Verkoopprijs" value={formatCurrency(product.sale_price)} />
                {isWebshop && <Row label="In open webshop-bestellingen" value={`${formatNumber(product.webshop_open_qty, 0)} ${product.unit}`} mono />}
                <Row label="Kostprijs" value={formatCurrency(product.cost_price)} />
                <Row label="Batchnummer" value={product.batch_number || "—"} mono />
                <Row label="THT" value={formatDate(product.expiry_date)} />
                <Row label="Laatste synchronisatie" value={formatDate(product.last_updated)} />
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="voorraad">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <WarehouseCard title="Kipkruiden" subtitle="Hoogeveenenweg 73 · Hoofdmagazijn en productielocatie"
              stock={product.stock_managed === false ? null : product.stock_kipkruiden} unit={product.unit}
              location={product.storage_location_kk} testId="wh-card-kipkruiden"
              note={isWebshop && "Berekend: webshoptotaal min Van den Brink"} />
            <WarehouseCard title="Van den Brink" subtitle="Zeewolde · Externe opslaglocatie"
              stock={product.stock_managed === false ? null : product.stock_vandenbrink} unit={product.unit}
              location={product.storage_location_vdb} testId="wh-card-vandenbrink"
              note={isWebshop && "Handmatig ingevuld door medewerkers"}
              action={isWebshop && product.stock_managed !== false && VDB_EDIT_ROLES.includes(role.id) && (
                <Button variant="outline" className="mt-4 h-12" onClick={() => setEditVdb(true)} data-testid="vdb-edit-detail">
                  <Pencil className="w-4 h-4 mr-2" /> Aantal aanpassen
                </Button>
              )} />
          </div>
          <VdbEditDialog product={product} open={editVdb} onOpenChange={setEditVdb} onSaved={setProduct} />
          <div className="mt-6 surface p-6">
            <h3 className="text-lg font-semibold mb-4">Recente batches</h3>
            <div className="divide-y divide-border">
              {product.batches.length === 0 && <div className="text-sm text-muted-foreground">Nog geen batches geregistreerd.</div>}
              {product.batches.slice(0, 10).map((b) => (
                <div key={b.id} className="py-3 flex items-center justify-between text-sm">
                  <div>
                    <span className="font-mono font-semibold">{b.batch_number}</span>
                    <span className="text-muted-foreground ml-3">{WAREHOUSE_LABEL[b.warehouse] || "—"}</span>
                  </div>
                  <div className="text-muted-foreground">
                    Ontvangen {formatDate(b.received_date)} · THT {formatDate(b.expiry_date)}
                  </div>
                  <div className="font-mono font-medium">{formatNumber(b.quantity)} {b.unit}</div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="bestellingen">
          <div className="surface overflow-hidden">
            {product.open_purchase_orders.length === 0 ? (
              <div className="p-12 text-center text-sm text-muted-foreground">Geen openstaande bestellingen voor dit product.</div>
            ) : (
              <table className="w-full text-base">
                <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Order</th>
                    <th className="px-4 py-3">Leverancier</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Productiedatum</th>
                    <th className="px-4 py-3">Verwachte levering</th>
                    <th className="px-4 py-3">Magazijn</th>
                    <th className="px-4 py-3 text-right">Aantal</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {product.open_purchase_orders.map((po) => {
                    const line = po.lines.find((l) => l.product_id === product.id);
                    return (
                      <tr key={po.id}>
                        <td className="px-4 py-3"><Link to={`/bestellingen/${po.id}`} className="font-mono font-semibold hover:text-primary">{po.po_number}</Link></td>
                        <td className="px-4 py-3">{po.supplier_name}</td>
                        <td className="px-4 py-3"><POStatusBadge status={po.status} /></td>
                        <td className="px-4 py-3 text-muted-foreground">{formatDate(po.production_date)}</td>
                        <td className="px-4 py-3 text-muted-foreground">{formatDate(po.expected_delivery_date)}</td>
                        <td className="px-4 py-3">{WAREHOUSE_LABEL[po.receive_warehouse]}</td>
                        <td className="px-4 py-3 text-right font-mono font-semibold">{line ? `${line.quantity} ${line.unit}` : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="verkoop">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <MetricCard label="Verkocht deze week" value={`${formatNumber(product.sold_last_week, 1)} ${product.unit}`} />
            <MetricCard label="Verkocht deze maand" value={`${formatNumber(product.sold_last_month, 1)} ${product.unit}`} />
            <MetricCard label="Gemiddeld per week" value={`${formatNumber(product.weekly_sales_avg, 1)} ${product.unit}`} />
          </div>
          <div className="mt-6 surface p-6">
            <h3 className="text-lg font-semibold mb-3">Verwachte voorraadduur</h3>
            {product.expected_weeks_remaining !== null ? (
              <p className="text-lg">
                Met de huidige beschikbare voorraad kan dit product naar verwachting nog{" "}
                <span className="font-mono font-semibold text-primary">{product.expected_weeks_remaining} weken</span>{" "}
                worden verkocht (op basis van gemiddelde verkoop van {product.weekly_sales_avg} {product.unit}/week).
              </p>
            ) : (
              <p className="text-muted-foreground">Onvoldoende verkoopdata om een verwachting te berekenen.</p>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MetricCard({ label, value, tone }) {
  const toneClass = {
    op_voorraad: "border-status-healthy/40",
    laag: "border-status-low/50",
    kritiek: "border-status-critical/40",
    niet_op_voorraad: "border-status-critical/40",
    "in-transit": "border-status-in-transit/40",
  }[tone] || "border-border";
  return (
    <div className={`surface p-6 ${toneClass}`}>
      <div className="label-eyebrow">{label}</div>
      <div className="mt-3 font-mono text-3xl md:text-4xl font-medium">{value}</div>
    </div>
  );
}

function WarehouseCard({ title, subtitle, stock, unit, location, note, action, testId }) {
  return (
    <div className="surface p-6" data-testid={testId}>
      <div className="flex items-center gap-3 mb-1">
        <Warehouse className="w-6 h-6 text-primary" />
        <h3 className="text-lg font-semibold">{title}</h3>
      </div>
      <div className="text-sm text-muted-foreground mb-4">{subtitle}</div>
      <div className="font-mono text-5xl font-semibold">{stock === null ? "—" : formatNumber(stock, 1)}</div>
      {stock === null && <div className="text-sm text-muted-foreground mt-1">Voorraadbeheer staat uit in de webshop</div>}
      <div className="text-sm text-muted-foreground mt-1">{unit}</div>
      {note && <div className="text-xs text-muted-foreground mt-2">{note}</div>}
      <div className="mt-4 text-sm">Opslaglocatie: <span className="font-mono font-medium">{location || "—"}</span></div>
      {action}
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between border-b border-border last:border-0 pb-2">
      <span className="text-muted-foreground">{label}</span>
      <span className={mono ? "font-mono font-medium" : "font-medium"}>{value || "—"}</span>
    </div>
  );
}
